'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Image } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';
import ReactImageGallery, {
  ReactImageGalleryItem,
  ReactImageGalleryProps,
} from 'react-image-gallery';

import './carousel.css';

import { ImageSrcSetSize } from '@/utils/image';

import FavoriteButton from '@/components/favorites/favorite-button';

import DriveImage, { getDriveImageURL } from './drive-image';

export interface ImageGalleryProps {
  images: Image[];
  listingUrl: string;
  clickTrackerUrl?: string | null;
  label?: string;
  imageSizes?: ImageSrcSetSize[];
  listingId: string;
  onFavoriteClick?: (listingId: string) => void;
}

export const ListingImages: React.FC<ImageGalleryProps> = (props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const galleryProps: ReactImageGalleryProps = {
    startIndex: currentIndex,
    lazyLoad: true,
    showPlayButton: false,
    showFullscreenButton: false,
    showBullets: true,
    slideDuration: 10,
    disableThumbnailScroll: true,
    disableSwipe: false,
    onErrorImageURL: props.images[0].url || '',
    infinite: true,
    renderLeftNav:
      currentIndex === 0
        ? (_onClick, disabled) => (
            <button
              aria-label="Navigate to previous image"
              disabled={disabled}
              type="button"
              className="image-gallery-left-nav text-white text-opacity-50 hover:text-opacity-100 hover:drop-shadow-lg"
              onClick={() => {
                setCurrentIndex(props.images.length - 1);
              }}
            >
              <svg
                className="rotate-180"
                width="14"
                height="24"
                viewBox="0 0 14 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
                />
              </svg>
            </button>
          )
        : (onClick, disabled) => (
            <button
              aria-label="Navigate to previous image"
              disabled={disabled}
              type="button"
              className="image-gallery-left-nav text-white text-opacity-50 hover:text-opacity-100 hover:drop-shadow-lg"
              onClick={onClick}
            >
              <svg
                className="rotate-180"
                width="14"
                height="24"
                viewBox="0 0 14 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
                />
              </svg>
            </button>
          ),
    renderRightNav:
      currentIndex === props.images.length - 1
        ? (_onClick, disabled) => (
            <button
              aria-label="Navigate to next image"
              disabled={disabled}
              type="button"
              className="image-gallery-right-nav text-white text-opacity-50 hover:text-opacity-100 hover:drop-shadow-lg"
              onClick={() => {
                setCurrentIndex(0);
              }}
            >
              <svg
                width="14"
                height="24"
                viewBox="0 0 14 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
                />
              </svg>
            </button>
          )
        : (onClick, disabled) => (
            <button
              aria-label="Navigate to next image"
              disabled={disabled}
              type="button"
              className="image-gallery-right-nav text-white text-opacity-50 hover:text-opacity-100 hover:drop-shadow-lg"
              onClick={onClick}
            >
              <svg
                width="14"
                height="24"
                viewBox="0 0 14 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
                />
              </svg>
            </button>
          ),
    onSlide: (currentIndex) => {
      setCurrentIndex(currentIndex);
    },
    renderCustomControls: () => (
      <div className="absolute z-[1] flex w-full flex-row justify-start p-3">
        <div
          className={clsx(
            !props.label && 'invisible',
            'ut-md flex h-fit cursor-default items-center whitespace-nowrap rounded-[2.5rem] bg-white bg-opacity-80 px-2 py-1.5 text-center text-gray-900 shadow-sm hover:bg-opacity-90'
          )}
        >
          {props.label}
        </div>
      </div>
    ),
    items: props.images.map((image, index) => ({
      image,
      original: getDriveImageURL({ url: image.url || '' }, { width: 1600 }),
      lazyLoad: true,
      loading: 'lazy',
      bulletClass:
        index < currentIndex - 3 || index > currentIndex + 3
          ? 'hidden-bullet'
          : index === currentIndex - 3 || index === currentIndex + 3
            ? 'edge'
            : '',
      thumbnailLoading: 'lazy',
      originalWidth: image.size?.width || 1600,
      originalHeight: image.size?.height || 1033,
      renderItem: (item: ReactImageGalleryItem & { image: Image }) => (
        <Link
          aria-label="listing-image"
          href={props.clickTrackerUrl || props.listingUrl}
          target="_blank"
        >
          <DriveImage
            className="image-gallery-item m-auto h-[15rem] w-full rounded-xl object-cover"
            image={item.image}
            width={item.image.size?.width || 1600}
            height={item.image.size?.height || 1033}
            alt={item.image.caption || `Image ${index + 1}`}
            loading="lazy"
            srcsetSizes={props.imageSizes}
          />
        </Link>
      ),
    })),
  };

  return (
    <div className="relative">
      <FavoriteButton
        onClick={props.onFavoriteClick}
        listingId={props.listingId}
      />
      <ReactImageGallery {...galleryProps} />
    </div>
  );
};
